import { lowerCase } from "lodash";
import pluralize from "@/lib/pluralize";
import { renderAttendanceType, renderParticipationRole, RATE_UNIT_MAP } from "@/pages/activities/activity-types/_utils";
import type {
  ActivityParticipationRole,
  AttendanceType,
  CreditRate,
  ParticipationActivityTypeRef,
  ParticipationLinkedActivityRef
} from "@/pages/activities/activity-types/types";
import type { ParticipationActivityDetails } from "./types";

export const CCMS_LEGACY_MANAGING_SECTION_NAME = "National Section";

const findMatchingCreditRate = (
  creditRates: CreditRate[],
  attendanceType?: AttendanceType | string,
  participationRole?: ActivityParticipationRole | string
) =>
  creditRates.find(
    (creditRate) => creditRate.attendanceType === attendanceType && creditRate.participationRole === participationRole
  );

const resolveParticipationCreditRate = (
  creditRates: CreditRate[] | undefined,
  attendanceType?: AttendanceType | string,
  participationRole?: ActivityParticipationRole | string
): CreditRate | undefined => {
  if (!creditRates?.length) {
    return undefined;
  }

  const exactMatch = findMatchingCreditRate(creditRates, attendanceType, participationRole);
  if (exactMatch) {
    return exactMatch;
  }

  return creditRates.length === 1 ? creditRates[0] : undefined;
};

const formatActivityDuration = (
  duration: number | null | undefined,
  creditRates?: CreditRate[],
  role?: ActivityParticipationRole | string,
  attendanceType?: AttendanceType | string
): string | undefined => {
  if (duration == null) {
    return undefined;
  }

  const matchedRate = resolveParticipationCreditRate(creditRates, attendanceType, role);
  if (!matchedRate?.rateUnit) {
    return String(duration);
  }

  return `${duration} ${pluralize(duration, lowerCase(matchedRate.rateUnit), "s")}`;
};

const formatParticipationCreditRate = (
  creditRates?: CreditRate[],
  role?: ActivityParticipationRole | string,
  attendanceType?: AttendanceType | string
): string | undefined => {
  const matchedRate = resolveParticipationCreditRate(creditRates, attendanceType, role);

  if (!matchedRate) {
    return undefined;
  }

  return `${matchedRate.rateMultiplier} / ${lowerCase(matchedRate.rateUnit)}`;
};

type MapperActivityType = Partial<Pick<ParticipationActivityTypeRef, "title" | "config">>;

type MapperLinkedActivity = Pick<
  ParticipationLinkedActivityRef,
  "title" | "allowParticipationStarting" | "allowParticipationThrough" | "duration" | "event"
> & {
  activityType?: MapperActivityType;
};

const resolveCreditRates = ({
  fetchedActivity,
  participationActivity,
  activityType
}: {
  fetchedActivity?: MapperLinkedActivity;
  participationActivity?: MapperLinkedActivity | null;
  activityType?: MapperActivityType | null;
}) =>
  fetchedActivity?.activityType?.config?.creditRates ??
  participationActivity?.activityType?.config?.creditRates ??
  activityType?.config?.creditRates;

const buildParticipationActivityDetails = ({
  isTypeLevelParticipation,
  activityType,
  participationActivity,
  fetchedActivity,
  participationTitle,
  series,
  role,
  attendanceType
}: {
  isTypeLevelParticipation: boolean;
  activityType?: MapperActivityType | null;
  participationActivity?: MapperLinkedActivity | null;
  fetchedActivity?: MapperLinkedActivity;
  participationTitle?: string;
  series?: string;
  role?: ActivityParticipationRole;
  attendanceType?: AttendanceType;
}): ParticipationActivityDetails | undefined => {
  const resolvedActivityType = activityType ?? participationActivity?.activityType ?? fetchedActivity?.activityType;
  const activityTypeTitle = resolvedActivityType?.title;
  const creditRates = resolveCreditRates({ fetchedActivity, participationActivity, activityType });

  if (isTypeLevelParticipation) {
    const details: ParticipationActivityDetails = {};

    if (participationTitle) {
      details.title = participationTitle;
    }
    if (activityTypeTitle) {
      details.activityType = activityTypeTitle;
    }
    if (series) {
      details.series = series;
    }

    return Object.keys(details).length > 0 ? details : undefined;
  }

  const activity = fetchedActivity ?? participationActivity;
  if (!activity && !activityTypeTitle && !series) {
    return undefined;
  }

  return {
    title: fetchedActivity?.title ?? participationActivity?.title ?? participationActivity?.event?.title,
    activityType: activityTypeTitle,
    ...(series ? { series } : {}),
    startDate:
      fetchedActivity?.allowParticipationStarting ??
      participationActivity?.allowParticipationStarting ??
      participationActivity?.event?.startsAt ??
      undefined,
    endDate:
      fetchedActivity?.allowParticipationThrough ??
      participationActivity?.allowParticipationThrough ??
      participationActivity?.event?.endsAt ??
      undefined,
    duration: formatActivityDuration(
      fetchedActivity?.duration ?? participationActivity?.duration,
      creditRates,
      role,
      attendanceType
    )
  };
};

const formatParticipationQuantity = (
  quantity: number | null | undefined,
  creditRates?: CreditRate[],
  role?: ActivityParticipationRole | string,
  attendanceType?: AttendanceType | string
): string | undefined => {
  if (quantity == null) {
    return undefined;
  }

  const matchedRate = resolveParticipationCreditRate(creditRates, attendanceType, role);

  if (matchedRate?.rateUnit) {
    const unitLabel = RATE_UNIT_MAP[matchedRate.rateUnit];
    const pluralizedUnit = quantity === 1 ? unitLabel : `${unitLabel}s`;
    return `${quantity} ${pluralizedUnit}`;
  }

  return String(quantity);
};

export const mapParticipationRateFields = ({
  role,
  attendanceType,
  quantity,
  isTypeLevelParticipation,
  activityType,
  participationActivity,
  fetchedActivity,
  participationTitle,
  series
}: {
  role?: ActivityParticipationRole;
  attendanceType?: AttendanceType;
  quantity?: number | null;
  isTypeLevelParticipation: boolean;
  activityType?: MapperActivityType | null;
  participationActivity?: MapperLinkedActivity | null;
  fetchedActivity?: MapperLinkedActivity;
  participationTitle?: string;
  series?: string;
}) => {
  const creditRates = resolveCreditRates({ fetchedActivity, participationActivity, activityType });
  const shouldShowQuantity = isTypeLevelParticipation || quantity != null;

  return {
    participationRole: role ? renderParticipationRole(role) : undefined,
    attendanceType: attendanceType ? renderAttendanceType(attendanceType) : undefined,
    quantity: shouldShowQuantity ? formatParticipationQuantity(quantity, creditRates, role, attendanceType) : undefined,
    creditRate: formatParticipationCreditRate(creditRates, role, attendanceType),
    activityDetails: buildParticipationActivityDetails({
      isTypeLevelParticipation,
      activityType,
      participationActivity,
      fetchedActivity,
      participationTitle,
      series,
      role,
      attendanceType
    })
  };
};
